/**
 * We use approach similar to `GNU gettext` where keys are written in plain English and used as fallback for missing translations.
 *  So that separate `id` is not needed, just the `key` string is used with `{{abc}}` for variables and `<1>text<1/>` for React components.
 *  So that from the developer's perspective, we still write in plain English and not with `id`s that are harder to understand and maintain.
 */

const keys = [
  'Portfolio',
  'Trade',
  'Developers',
  'Docs',
  'API Docs',
  'Code Samples',
  'More',
  'Stats',
  'Join Discord',
  'Notifications',
  'All caught up!',
  'You have no new notifications',
  'Display Toast Notifications',
  'Connect Wallet',
  'Connect your Wallet',
  'Choose Wallet',
  'Link Wallet',
  'You will receive a signature request. Signing is free and will not send a transaction.',
  'Generate Private Key',
  'Generating Private Key',
  'Private Key Generated',
  'Enable secure access to our API',
  'Remember Me',
  'Only use “Remember Me” if you are using a secure device you own. Selecting this option can expose your information to  others if you are accessing this wallet from a public device.',
  'Continue',
  'Disconnect Wallet',
  'Disconnect',
  'Account',
  'Username',
  'Edit',
  'Your username is now hidden. It is not visible to other users.',
  'Edit Your Username',
  'Your username will be visible to other users unless you choose to hide it. You can always change it later.',
  'Hide my Username',
  'Save',
  'Username is required',
  'Username must be between 5 and 20 characters',
  'Username can only contain letters and numbers',
  'Wallets',
  'L1 Address',
  'L2 Address',
  'Open in Explorer',
  'Sign Out',
  'Copy L2 Private Key',
  'No markets available',
  'Last Price',
  'Open Interest',
  '24h Volume',
  'Mark Price',
  'Spot Oracle Price',
  'Eight hour funding rate that is continuously accrued on positions and settled upon any trade',
  '8h Funding Rate',
  'Limit',
  'LIMIT',
  'Market',
  'MARKET',
  'Limit Price',
  'Amount',
  'Your order stays open until it is filled or you decide to cancel.',
  'Your order gets filled right away, partially or fully, and whatever is left gets canceled instantly.',
  'Fee',
  'Currently the fees are fixed and uniform across the platform. Maker {{makerFee}} / Taker {{takerFee}}',
  'Notional',
  'Buy',
  'BUY',
  'Sell',
  'SELL',
  'Account Value',
  'The total value of your account at current mark prices',
  'Unrealized P&L',
  'Free Collateral',
  'Account Leverage',
  'Margin',
  'Initial Margin',
  'Maintenance Margin',
  'Account value / maintenance margin requirements',
  'Health Factor',
  'Order Book',
  'Trades',
  'Price',
  'Size',
  'Total',
  'Time',
  'Spread',
  'Positions',
  'Open Orders',
  'Trade History',
  'Order History',
  'Funding Payments',
  'Side',
  'Entry Price',
  'Unrealized Funding',
  'Close Position',
  'Type',
  'Filled',
  'Instruction',
  'Client Order Id',
  'Status',
  'Notional / Fee',
  'Liquidity',
  'Tx Hash',
  'Id',
  'Remaining',
  'Cancel Reason',
  'Order Id',
  'Payment',
  'Cancel All',
  '{{orderSize}} will be closed at Market price',
  'By connecting a wallet, you agree to <1>Paradex Terms of Service</1> and represent and warrant to Paradex that you are not a <2>Restricted Person</2>.',
  'Wallet',
  'Deposit',
  'Withdraw',
  'Withdrawal',
  'Collateral Value',
  'Position Notional',
  'Collateral',
  'Asset',
  'Wallet Balance',
  'Actions',
  'Action',
  'Tx Hash L2',
  'Tx Hash L1',
  'Withdraw to Wallet',
  'Deposit to Paraclear',
  'You have no balance in your Ethereum wallet. Deposit some {{asset}} and try again.',
  'It is not possible to deposit more than your current balance.',
  'Available on L1',
  'Deposits are a two-step process. First initiate a deposit from your Ethereum wallet',
  'Deposits are a two-step process. First initiate a deposit from your Ethereum wallet to the Starknet bridge. Second, initiate deposit from the Starknet bridge to Paraclear via the Deposit / Withdraw table. These transactions may take several minutes depending on network conditions. For more information check out our <1>docs</1>.',
  'read more',
  'Initiate Deposit to Bridge',
  'Enable USDC on Paradex',
  'In order to deposit you must allow Paradex access to at least {{amount}}.',
  'Enable USDC',
  'Withdraw initiated',
  'Withdrawals are a two-step process. First initiate a withdrawal from Paraclear',
  'Withdrawals are a two-step process. First initiate a withdrawal from Paraclear to the Starknet bridge. This first step can take 2-7 hours. Second, initiate your withdrawal from the Starknet bridge to your Ethereum wallet via the Deposit / Withdraw table. For more information check out our <1>docs</1>.',
  'Initiate Withdrawal to Bridge',
  'You have no funds to withdraw. It is not possible to withdraw more than your free collateral.',
  'It is not possible to withdraw more than your free collateral.',
  'You have no collateral',
  'You have no open positions',
  'You have no open orders',
  'You have no trades',
  'You have no orders',
  'You have no funding payments',
  'You have no transfers',
  'Account in liquidation',
  'Account is being liquidated. Any open positions will be closed. Trading now is disabled.',
  'Account liquidated',
  'Your account was liquidated on {{createdAt}}. Any open positions were closed.',
  'Deposit to Paraclear successful',
  'Deposit to Paraclear failed',
  'Unexpected error. Try again.',
  'Deposit to bridge initiated',
  'Deposit to Paraclear available',
  'Go to Deposits',
  'Deposit to Paraclear in progress',
  '<1>Paradex Closed Beta</1> This is a CLOSED BETA version of Paradex, the product is still being tested and can be subject to bugs and errors. For more details please read carefully the <2>Paradex terms of service</2> & <3>documentation</3>.',
  'Limit {{side}} Order Fill',
  '{{type}} {{side}} Order Submitted',
  '{{type}} {{side}} Order Canceled',
  'Reason',
  'Market {{side}} Order Fill',
  'Trade Busted On-Chain',
  'Fill id',
  'Withdraw to wallet failed',
  'Withdrawal to bridge initiated',
  'Withdrawal error. Try again.',
  'Withdrawal to bridge in progress',
  'Withdrawal to wallet initiated',
  'Withdrawal to bridge failed',
  'Transaction error. Try again.',
  'Withdrawal to wallet available',
  'Go to Withdrawals',
  'Withdrawal to wallet successful',
  'Session expired, please connect your wallet again.',
  'Failed to submit order',
  'Your username preferences were updated!',
  'Unexpected error updating username preferences',
  'This username already exists. Please choose a different username and try again.',
] as const;

type Keys = typeof keys[number];
type Translations = { [K in Keys]: K };

const translations: Translations = Object.fromEntries(
  keys.map((key) => [key, key]),
) as Translations;

export type { Keys };
export default translations;
